import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-hot-toast';
import apiService from '../../services/apiService';

const VoiceAgents = () => {
    const [agents, setAgents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedAgent, setSelectedAgent] = useState(null);
    const [profiles, setProfiles] = useState([]);
    const [selectedLang, setSelectedLang] = useState('en-US');
    const [editProfile, setEditProfile] = useState(null);

    // Fetch Agents
    const fetchAgents = async () => {
        try {
            const data = await apiService.makeRequest('/voice/agents');
            setAgents(data);
            if (data.length > 0) setSelectedAgent(data[0]);
        } catch (error) {
            toast.error("Failed to load agents");
        } finally {
            setLoading(false);
        }
    };



    // Fetch Profiles for Selected Agent
    const fetchProfiles = async () => {
        if (!selectedAgent) return;
        try {
            const data = await apiService.makeRequest(`/voice/agents/${selectedAgent.id}/profiles`);
            setProfiles(data);

            // Set initial edit state
            const currentProfile = data.find(p => p.language_code === selectedLang);
            if (currentProfile) {
                setEditProfile(currentProfile);
            } else {
                // Default empty for new lang
                setEditProfile({
                    language_code: selectedLang,
                    system_prompt: "",
                    first_message: "",
                    voice_id: "",
                    voice_provider: "deepgram",
                    tools_config: []
                });
            }
        } catch (error) {
            console.error(error);
        }
    };

    useEffect(() => {
        fetchAgents();
    }, []);

    useEffect(() => {
        fetchProfiles();
    }, [selectedAgent, selectedLang]);

    const handleSave = async () => {
        if (!selectedAgent) return;
        try {
            await apiService.makeRequest(`/voice/agents/${selectedAgent.id}/profiles/${selectedLang}`, {
                method: 'PUT',
                body: JSON.stringify(editProfile),
            });
            toast.success(`Saved ${selectedLang} profile!`);
            fetchProfiles();
        } catch (error) {
            toast.error("Save failed");
        }
    };

    // [SECURITY] Robust Check via Context
    const { user } = useAuth();

    // Safety check: wait for user to load if needed, or just check role
    if (!user || user.role !== 'admin') {
        return null;
    }

    if (loading) {
        return (
            <div className="bg-white border border-[#E5E1D6] rounded-xl p-6 text-center text-sm text-[#687077]">
                Loading Voice Brain...
            </div>
        );
    }

    return (
        <div 
            className="bg-white border border-[#E5E1D6] rounded-xl shadow-sm p-6 space-y-6 text-[#20231F]"
            style={{ borderTop: '3px solid #C8A653' }}
        >
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-b border-[#E5E1D6] pb-4">
                <div>
                    <h3 className="text-lg font-bold text-[#20231F] flex items-center gap-2">
                        <span>🧠</span> Voice Brain Center <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-[#FAF4E5] text-[#C8A653] border border-[#E5E1D6]">Admin</span>
                    </h3>
                    <p className="text-xs text-[#687077] mt-0.5">
                        Manage underlying AI agents, languages, voice IDs, and system prompts
                    </p>
                </div>
                <div className="flex flex-wrap gap-1.5">
                    {agents.map(agent => (
                        <button
                            key={agent.id}
                            type="button"
                            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors cursor-pointer ${
                                selectedAgent?.id === agent.id
                                    ? 'bg-[#20231F] text-white shadow-xs'
                                    : 'bg-[#F8F7F2] text-[#687077] hover:bg-[#EAE7DC] border border-[#E5E1D6]'
                            }`}
                            onClick={() => setSelectedAgent(agent)}
                        >
                            {agent.name}
                        </button>
                    ))}
                </div>
            </div>

            <div>
                {selectedAgent && (
                    <div className="space-y-4">
                        {/* Language Tabs */}
                        <div className="flex flex-wrap gap-2">
                            {['en-US', 'ru-RU', 'es-ES'].map(lang => (
                                <button
                                    key={lang}
                                    type="button"
                                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors cursor-pointer border ${
                                        selectedLang === lang
                                            ? 'bg-[#FAF4E5] text-[#20231F] border-[#C8A653] font-semibold'
                                            : 'bg-white text-[#687077] border-[#E5E1D6] hover:bg-[#F8F7F2]'
                                    }`}
                                    onClick={() => setSelectedLang(lang)}
                                >
                                    {lang === 'en-US' ? '🇺🇸 English' : lang === 'ru-RU' ? '🇷🇺 Russian' : '🇪🇸 Spanish'}
                                </button>
                            ))}
                        </div>

                        {/* Editor */}
                        <div className="space-y-1.5">
                            <label className="text-xs font-bold uppercase tracking-wider text-[#687077]">
                                System Prompt (Personality & Rules)
                            </label>
                            <textarea
                                className="w-full rounded-lg border border-[#E5E1D6] bg-[#FCFBF8] p-3 text-sm font-mono text-[#20231F] focus:outline-none focus:border-[#C8A653] focus:ring-1 focus:ring-[#C8A653] transition-colors"
                                rows="8"
                                value={editProfile?.system_prompt || ""}
                                onChange={(e) => setEditProfile({ ...editProfile, system_prompt: e.target.value })}
                            />
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="space-y-1.5">
                                <label className="text-xs font-bold uppercase tracking-wider text-[#687077]">
                                    First Message (Greeting)
                                </label>
                                <input
                                    type="text"
                                    className="w-full rounded-lg border border-[#E5E1D6] bg-[#FCFBF8] px-3 py-2 text-sm text-[#20231F] focus:outline-none focus:border-[#C8A653] focus:ring-1 focus:ring-[#C8A653] transition-colors"
                                    value={editProfile?.first_message || ""}
                                    onChange={(e) => setEditProfile({ ...editProfile, first_message: e.target.value })}
                                />
                            </div>
                            <div className="space-y-1.5">
                                <label className="text-xs font-bold uppercase tracking-wider text-[#687077]">
                                    Voice ID
                                </label>
                                <input
                                    type="text"
                                    className="w-full rounded-lg border border-[#E5E1D6] bg-[#FCFBF8] px-3 py-2 text-sm text-[#20231F] focus:outline-none focus:border-[#C8A653] focus:ring-1 focus:ring-[#C8A653] transition-colors"
                                    value={editProfile?.voice_id || ""}
                                    onChange={(e) => setEditProfile({ ...editProfile, voice_id: e.target.value })}
                                    placeholder="e.g. aura-helios-en"
                                />
                            </div>
                        </div>

                        <div className="pt-2">
                            <button 
                                type="button"
                                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#20231F] hover:bg-black text-white text-xs font-bold transition-colors cursor-pointer shadow-sm" 
                                onClick={handleSave}
                            >
                                💾 Save Brain Configuration
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default VoiceAgents;
